import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ArrowRight, Check, Lock, Flame } from "lucide-react";
import { toast } from "sonner";

/*  /founding — the Founding Crew Pass drop.
    Three tiers: $49 (25 seats) → $79 (50) → $99 (100). Price climbs as
    seats fill. Honest scarcity — seat counts come from the live DB on
    every page load. "Seasons 1-3 included + founder pricing after" to
    avoid the "lifetime" legal landmine.
*/
const Founding = () => {
  const [status, setStatus]       = useState(null);
  const [email, setEmail]         = useState("");
  const [name, setName]           = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/founding/status")
      .then((r) => setStatus(r.data))
      .catch(() => toast.error("Couldn't load the live seat count."));
  }, []);

  const buy = async () => {
    if (!email.trim()) { toast.error("Email is required."); return; }
    setSubmitting(true);
    try {
      const sp = new URLSearchParams(window.location.search);
      const referred_by = (sp.get("ref") || "").toUpperCase();
      const { data } = await api.post("/founding/checkout", {
        email: email.trim(),
        name: name.trim(),
        origin_url: window.location.origin,
        referred_by,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed.");
      setSubmitting(false);
    }
  };

  if (!status) return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#d4af37]" />
    </div>
  );

  const active = status.tiers.find((t) => t.state === "active");

  return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] px-4 sm:px-6 py-10 sm:py-16" data-testid="founding-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="text-[10px] tracking-[0.3em] text-[#5a5050] hover:text-[#d4af37]">← wasntme.party</Link>

        {/* Hero */}
        <div className="mt-6 mb-10">
          <div className="text-[10px] tracking-[0.4em] text-[#8b0000] mb-3 flex items-center gap-2">
            <Flame size={12} /> FOUNDING CREW · LIMITED SEATS
          </div>
          <h1 className="font-display text-4xl sm:text-6xl text-[#f4f0ec] leading-tight mb-4">
            Lock in a founder seat <span className="text-[#d4af37]">before the price moves.</span>
          </h1>
          <p className="text-sm sm:text-base text-[#a39797] leading-relaxed max-w-2xl">
            Seasons 1–3 included forever. Founder pricing on every case after. Priority access to Last Alibi Standing on New Year's Eve. 175 seats total. Price goes up as seats sell.
          </p>
        </div>

        {/* Live counter */}
        <div className="card-noir p-5 mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="founding-counter">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-[#a39797] mb-1">SEATS FILLED</div>
            <div className="font-display text-3xl text-[#f4f0ec]">
              {status.total_sold} <span className="text-[#5a5050] text-xl">/ {status.total_ceiling}</span>
            </div>
          </div>
          <div className="w-full sm:flex-1 sm:max-w-md">
            <div className="h-2 bg-[#1a1515] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#8b0000] to-[#d4af37] transition-all duration-500"
                style={{ width: `${status.percent_filled}%` }}
              />
            </div>
            <div className="text-[10px] text-[#5a5050] mt-1.5 text-right">{status.percent_filled}% filled</div>
          </div>
        </div>

        {/* Tier ladder */}
        <div className="grid md:grid-cols-3 gap-4 mb-10" data-testid="founding-tiers">
          {status.tiers.map((t) => (
            <div
              key={t.id}
              className={`card-noir p-5 relative ${t.state === "active" ? "border-[#d4af37] shadow-lg" : "opacity-60"}`}
              data-testid={`founding-tier-${t.id}`}
            >
              {t.state === "active" && (
                <div className="absolute -top-3 left-5 bg-[#d4af37] text-black text-[9px] tracking-[0.3em] px-2 py-1 font-semibold">
                  ACTIVE
                </div>
              )}
              {t.state === "sold_out" && (
                <div className="absolute -top-3 left-5 bg-[#8b0000] text-[#f4f0ec] text-[9px] tracking-[0.3em] px-2 py-1 font-semibold">
                  SOLD OUT
                </div>
              )}
              {t.state === "locked" && (
                <div className="absolute -top-3 left-5 bg-[#1a1515] border border-[#2a2525] text-[#5a5050] text-[9px] tracking-[0.3em] px-2 py-1 flex items-center gap-1">
                  <Lock size={9} /> LOCKED
                </div>
              )}
              <div className="text-[10px] tracking-[0.3em] text-[#a39797] mb-1 mt-2">{t.label}</div>
              <div className="font-display text-5xl text-[#f4f0ec] mb-2">{t.price_label}</div>
              <div className="text-xs text-[#a39797] mb-3">
                {t.state === "active" ? (
                  <span className="text-[#d4af37]">{t.left} of {t.seats} left</span>
                ) : t.state === "sold_out" ? (
                  <>All {t.seats} seats sold</>
                ) : (
                  <>Unlocks after Tier {t.id === "t2" ? 1 : 2} sells out</>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* What's included */}
        <div className="card-noir p-6 mb-10" data-testid="founding-includes">
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">WHAT YOU GET</div>
          <ul className="space-y-2">
            {status.includes.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#d4d0cc]">
                <Check size={14} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Checkout */}
        {status.sold_out ? (
          <div className="card-noir p-8 text-center" data-testid="founding-sold-out">
            <div className="text-sm text-[#8b0000] tracking-[0.3em] mb-2">PASS CLOSED</div>
            <div className="text-sm text-[#a39797]">All 175 seats are gone. The next pass opens with Season 4.</div>
          </div>
        ) : (
          <div className="card-noir p-6 sm:p-8 space-y-4" data-testid="founding-checkout">
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
              CLAIM SEAT #{status.total_sold + 1} · {active?.price_label} · {active?.left} LEFT IN TIER
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2.5 text-sm text-[#f4f0ec] focus:border-[#d4af37] outline-none"
                data-testid="founding-email"
              />
              <input
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Detective name (optional)"
                maxLength={80}
                className="bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2.5 text-sm text-[#f4f0ec] focus:border-[#d4af37] outline-none"
                data-testid="founding-name"
              />
            </div>
            <button
              onClick={buy} disabled={submitting}
              className="btn-primary w-full py-3"
              data-testid="founding-buy"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              {submitting ? "Opening checkout…" : `Claim my seat → ${active?.price_label}`}
              <ArrowRight size={14} />
            </button>
            <div className="text-[10px] text-[#5a5050] leading-relaxed">
              One-time payment. "Founder pricing" = any case released after Season 3 is yours for max $19. Not sold as "lifetime access" — if the business pivots or closes, we honor your Season 1-3 downloads for 10 years after purchase.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Founding;
